<template>
  <transition>
    <div
      v-if="isOpen"
      class="alert"
      :class="classes">
      <div
        class="alert__body"
        :class="bodyClass">
        <div
          v-if="icon"
          class="alert__icon">
          <a-icon :icon="icon" />
        </div>

        <div class="alert__content">
          <slot />
        </div>

        <div class="alert__dismiss-wrapper">
          <button
            v-if="dismissible"
            class="alert__dismiss"
            title="Dismiss alert"
            @click="onDismiss">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512">
              <path
                fill="currentColor"
                d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
            </svg>
          </button>
        </div>
      </div>

      <a-progress-bar
        v-if="showProgressBar"
        size="xs"
        :variant="variant"
        :percentage="progressPercent" />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
// @ts-ignore
import anime from "animejs";

const emits = defineEmits( ["update:modelValue", "dismiss"] );

const props = withDefaults( defineProps<{
  /**
   * The model value for the alert component.
   */
  modelValue?: boolean;
  /**
   * The variant of the alert component.
   */
  variant?: string;
  /**
   * Whether the alert component is dismissible.
   */
  dismissible?: boolean;
  /**
   * The auto dismiss time in milliseconds.
   */
  autoDismissTime?: number | null;
  /**
   * Whether to show the progress bar.
   */
  showProgressBar?: boolean;
  /**
   * An icon to show in the alert
   */
  icon?: string;
  /**
   * Any classes to set on the alert body
   */
  bodyClass?: string;
}>(), {
  modelValue: true,
  variant: "primary",
  dismissible: false,
  autoDismissTime: null,
  showProgressBar: false,
} );

// Define the reactive variables
const isOpen = ref( props.modelValue );
const progressPercent = ref( 100 );

const classes = computed( () => {
  const result = ["alert-" + props.variant];

  if ( props.dismissible ) {
    result.push( "alert--dismissible" );
  }

  return result;
} );

// Watch for changes in the modelValue prop and update the isOpen variable
watch( () => props.modelValue, ( val ) => {
  isOpen.value = val;
} );

// Watch for changes in the isOpen variable and emit the updated value
watch( isOpen, ( val ) => {
  emits( "update:modelValue", val );
} );

// Handle the dismiss event
function onDismiss () {
  isOpen.value = false;
  emits( "dismiss" );
}

// Perform the auto-dismiss animation when isOpen and autoDismissTime are set
if ( import.meta.client ) {
  watch( isOpen, ( val ) => {
    if ( val && props.autoDismissTime ) {
      const percentage = {
        progress: 100,
      };

      anime( {
        targets: percentage,
        progress: 0,
        easing: "linear",
        duration: props.autoDismissTime,
        update: () => {
          progressPercent.value = percentage.progress;
        },
        complete () {
          onDismiss();
        },
      } );
    }
  }, { immediate: true } );
}
</script>

<style scoped lang="scss">
.alert {
  padding: var(--s-4);
  border-radius: .5rem;
  border-width: 1px;
  border-style: solid;
  position: relative;
  overflow: hidden;

  &__body {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-4);
  }

  &__body.container {
    @media(max-width: 767px) {
      padding: 0;
    }
  }

  &__icon {
    display: flex;
    flex-grow: 0;
  }

  &__content {
    flex-grow: 1;
  }

  &__dismiss-wrapper {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    display: none;
  }

  &__dismiss {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: flex-start;
    width: 2rem;
    height: 2rem;
    background-color: transparent;
    border: none;
    color: inherit;
    outline: .2rem solid transparent;
    border-radius: .25rem;
    transition: outline-color var(--transition-time) ease-out;

    &:hover {
      cursor: pointer;
      opacity: 0.7;
    }

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  &--dismissible &__dismiss-wrapper {
    display: block;
  }

  .progress-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    --border-radius: 0 .25rem .25rem 0;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
